import primaryComponents from "../../../../components/primaryComponents";
import secondaryComponents from "../../../../components/secondaryComponents";
import { useAccountingTrialBalance } from "./useAccountingTrialBalance";
import { formatCurrency } from "../../../../helpers";
import styles from "./styles.module.scss";

const AccountingTrialBalance = () => {
  const {
    asOfDate,
    reportData,
    isLoadingReport,
    storeList,
    storeHasMultipleCurrency,
    currentStore,
    setDateFilter,
    filterByStoreId,
    fetchTrialBalanceReport,
  } = useAccountingTrialBalance();

  const currencyCode = reportData?.currency_code || "NGN";
  const formatAmount = (value) =>
    formatCurrency({ value: value ?? 0, currencyCode });

  return (
    <>
      <div className="d-flex justify-content-between p-3">
        <div style={{ width: "200px" }}>
          <primaryComponents.SelectField
            options={
              storeHasMultipleCurrency
                ? [...storeList]
                : [
                    {
                      name: "All stores",
                      id: "ALL_STORES",
                    },
                    ...storeList,
                  ]
            }
            idKey="id"
            nameKey="name"
            onChange={(value) => {
              filterByStoreId(value);
            }}
            value={
              storeList?.find(
                (storeItem) => `${storeItem?.id}` === `${currentStore}`,
              )?.name || "All stores"
            }
            floatError={false}
            classNames="store-select"
          />
        </div>
        <div className="d-flex align-items-center" style={{ gap: "12px" }}>
          <div>
            <label htmlFor="">As of Date</label>
            <primaryComponents.DateInput
              name="as_of_date"
              value={asOfDate || ""}
              onChange={({ target: { value } }) => {
                setDateFilter(value);
              }}
              placeholder="Select date"
            />
          </div>
          <div>
            <primaryComponents.Button
              classNames="btn btn--primary"
              onClick={fetchTrialBalanceReport}
              isLoading={isLoadingReport}
              loadingText="Generating..."
            >
              Generate Report
            </primaryComponents.Button>
          </div>
        </div>
      </div>

      <secondaryComponents.LoaderHelper
        isLoading={isLoadingReport}
        classNames="mt-4"
      >
        <div className={styles.reportCard}>
          {/* Table */}
          <table className={styles.reportTable}>
            <thead>
              <tr>
                <th>Account</th>
                <th className={styles.reportTable__amountCol}>Debit</th>
                <th className={styles.reportTable__amountCol}>Credit</th>
              </tr>
            </thead>
            <tbody>
              {(reportData?.groups || []).length === 0 && !isLoadingReport && (
                <tr>
                  <td colSpan={3} className={styles.emptyState}>
                    No data available. Set a date and click Generate Report.
                  </td>
                </tr>
              )}

              {(reportData?.groups || []).map((group, gIdx) => (
                <>
                  {/* Group Header */}
                  <tr key={`group-${gIdx}`} className={styles.sectionHeader}>
                    <td colSpan={3}>{group.account_type_label}</td>
                  </tr>

                  {/* Account Line Items */}
                  {(group.accounts || []).map((account, aIdx) => (
                    <tr
                      key={`account-${gIdx}-${aIdx}`}
                      className={styles.lineItem}
                    >
                      <td className={styles.lineItem__name}>
                        <span className={styles.lineItem__code}>
                          {account.account_code}{" "}
                        </span>
                        {account.account_name}
                      </td>
                      <td className={styles.lineItem__amount}>
                        {account.net_debit !== 0
                          ? formatAmount(account.net_debit)
                          : ""}
                      </td>
                      <td className={styles.lineItem__amount}>
                        {account.net_credit !== 0
                          ? formatAmount(account.net_credit)
                          : ""}
                      </td>
                    </tr>
                  ))}
                </>
              ))}

              {(reportData?.groups || []).length > 0 && (
                <tr className={styles.highlightRow}>
                  <td>
                    Total{" "}
                    {reportData?.is_balanced ? (
                      <span className={styles.balancedBadge}>Balanced</span>
                    ) : (
                      <span className={styles.unbalancedBadge}>
                        {" "}
                        Unbalanced
                      </span>
                    )}
                  </td>
                  <td>{formatAmount(reportData?.total_net_debit)}</td>
                  <td>{formatAmount(reportData?.total_net_credit)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </secondaryComponents.LoaderHelper>
    </>
  );
};

export default AccountingTrialBalance;
